// @ts-nocheck
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { useNetworkAndApiErrors } from 'hooks/error';

import Error from 'support/Error';
import textToSpeechManager from 'support/tts/TextToSpeechManager';

import LoadingOverlay from 'components/common/LoadingOverlay';

import ErrorModal from 'components/common/ErrorModal';

const messages = {
  loadingTts: {
    id: 'withLoading.tts',
    description: 'TTS for spinner',
  },
};

// Takes care of showing a loading spinner or error modal.
// The loading status (boolean) and error must be provided.
const WithLoading = ({
  loading,
  error,
  renderLoading,
  renderError,
  children,
  positiveCTA,
  negativeCTA,
  onRetry,
  onCancel,
  ttsText,
  enableTts,
}) => {
  const intl = useIntl();
  const errorObject = error ? new Error(error) : null;
  const { title, message } = useNetworkAndApiErrors(errorObject);

  useEffect(() => {
    if (loading && enableTts) {
      textToSpeechManager.add(
        ttsText || intl.formatMessage(messages.loadingTts),
      );
      textToSpeechManager.speak();
    }
  }, [enableTts, intl, loading, ttsText]);

  const hasRetried = useRef(false);
  useEffect(() => {
    if (
      errorObject &&
      errorObject.isConnectionError() &&
      !hasRetried.current &&
      onRetry
    ) {
      hasRetried.current = true;
      onRetry();
    }
  }, [errorObject, onRetry]);

  const history = useHistory();

  return (
    <React.Fragment>
      {children}
      {loading && (renderLoading ? renderLoading() : <LoadingOverlay />)}
      {!renderError && (
        <ErrorModal
          isOpen={!!error}
          title={title || 'Error'}
          message={message || 'Something went wrong.  Please try again.'}
          onCancel={
            onCancel ||
            (errorObject &&
              !errorObject.isConnectionError() &&
              (() => history.goBack())) ||
            null
          }
          onRetry={onRetry}
          positiveCTA={positiveCTA}
          negativeCTA={negativeCTA}
        />
      )}
      {error && renderError && renderError()}
    </React.Fragment>
  );
};

WithLoading.propTypes = {
  loading: PropTypes.bool,
  renderLoading: PropTypes.func,
  error: PropTypes.shape({
    title: PropTypes.string,
    message: PropTypes.string,
    statusCode: PropTypes.number,
  }),
  renderError: PropTypes.func,
  children: PropTypes.node,
  onRetry: PropTypes.func,
  onCancel: PropTypes.func,
  positiveCTA: PropTypes.string,
  negativeCTA: PropTypes.string,
  ttsText: PropTypes.string,
  enableTts: PropTypes.bool,
};

WithLoading.defaultProps = {
  loading: false,
  renderLoading: null,
  error: null,
  renderError: null,
  onRetry: null,
  onCancel: null,
  positiveCTA: null,
  negativeCTA: null,
  children: null,
  ttsText: null,
  enableTts: true,
};

export default WithLoading;
